import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Alert,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Delete, Block, CheckCircle, MoreVert, Edit } from '@mui/icons-material';
import { useAuth } from '@clerk/clerk-react';
import { getUsers, deactivateUser, deleteUser, updateUserRole } from '../../services/adminService';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'active' | 'inactive' | 'pending';
  role: 'admin' | 'user';
  createdAt: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingRoleUserId, setEditingRoleUserId] = useState<string | null>(null);
  const { getToken } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      const usersData = await getUsers(token);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [getToken]);

  const handleOpenDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      await deleteUser(selectedUser.id, token);
      setUsers(users.filter(user => user.id !== selectedUser.id));
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      setLoading(true);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      await deactivateUser(user.id, user.status === 'active', token);
      
      setUsers(users.map(u => {
        if (u.id === user.id) {
          return {
            ...u,
            status: user.status === 'active' ? 'inactive' : 'active'
          };
        }
        return u;
      }));
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Failed to update user status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: string) => {
    try {
      setLoading(true);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      const updatedUser = await updateUserRole(userId, newRole, token);
      
      setUsers(users.map(u => {
        if (u.id === userId) {
          return {
            ...u,
            role: updatedUser.role
          };
        }
        return u;
      }));
      
      setEditingRoleUserId(null);
    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRoleEdit = () => {
    setEditingRoleUserId(null);
  };

  // Handle escape key to cancel role editing
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && editingRoleUserId) {
        handleCancelRoleEdit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editingRoleUserId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  // Safe date formatting helper
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">User Management</Typography>
        <Button
          variant="outlined"
          onClick={loadUsers}
          disabled={loading}
        >
          Refresh List
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No users found
          </Typography>
        </Paper>
      ) : isMobile ? (
        // Mobile card view
        <Grid container spacing={2}>
          {users.map((user) => (
            <Grid item xs={12} key={user.id}>
              <Card sx={{ mb: 1, borderRadius: 2 }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Chip 
                      label={user.status.toUpperCase()} 
                      size="small" 
                      color={getStatusColor(user.status) as "success" | "error" | "warning" | "default"}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                  
                  <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                    {editingRoleUserId === user.id ? (
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={user.role}
                          onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                          disabled={loading}
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip 
                        label={user.role.toUpperCase()} 
                        size="small" 
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        onClick={() => setEditingRoleUserId(user.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      Created: {formatDate(user.createdAt)}
                    </Typography>
                  </Stack>
                  
                  <Divider sx={{ mt: 1, mb: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                    <Tooltip title={user.status === 'active' ? "Deactivate" : "Activate"}>
                      <span>
                        <IconButton
                          onClick={() => handleToggleUserStatus(user)}
                          color={user.status === 'active' ? 'error' : 'success'}
                          disabled={user.role === 'admin'}
                          size="small"
                        >
                          {user.status === 'active' ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          onClick={() => handleOpenDeleteDialog(user)}
                          color="error"
                          disabled={user.role === 'admin'}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Desktop table view
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created On</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {editingRoleUserId === user.id ? (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={user.role}
                          onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                          disabled={loading}
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip 
                        label={user.role.toUpperCase()} 
                        size="small" 
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        onClick={() => setEditingRoleUserId(user.id)}
                        sx={{ cursor: 'pointer' }}
                        icon={<Edit sx={{ fontSize: '14px !important' }} />}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.status.toUpperCase()} 
                      size="small" 
                      color={getStatusColor(user.status) as "success" | "error" | "warning" | "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(user.createdAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={user.status === 'active' ? "Deactivate" : "Activate"}>
                      <span>
                        <IconButton
                          onClick={() => handleToggleUserStatus(user)}
                          color={user.status === 'active' ? 'error' : 'success'}
                          disabled={user.role === 'admin'}
                        >
                          {user.status === 'active' ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          onClick={() => handleOpenDeleteDialog(user)}
                          color="error"
                          disabled={user.role === 'admin'}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user {selectedUser?.firstName} {selectedUser?.lastName} ({selectedUser?.email})? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 